import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookImage } from '../types';
import { getImageUrl, getCardImageUrl, getDetailImageUrl } from '../services/api';
import './ImageGallery.css';

// Fallback onError: variante otimizada -> original (uma única tentativa, sem loopar)
function handleImageFallback(event: React.SyntheticEvent<HTMLImageElement>, originalUrl: string): void {
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === 'true') return;
  img.dataset.fallbackApplied = 'true';
  img.src = originalUrl;
}

export interface ImageGalleryProps {
  /** Array de imagens a serem exibidas */
  images: BookImage[];
  /** Título do livro para acessibilidade */
  boxTitle?: string;
  /** Índice inicial da imagem (opcional) */
  initialIndex?: number;
  /** Callback quando o índice da imagem muda */
  onImageChange?: (index: number) => void;
  /** Habilitar zoom na imagem principal */
  enableZoom?: boolean;
  /** Mostrar indicadores de posição (dots) */
  showDots?: boolean;
  /** Mostrar thumbnails */
  showThumbnails?: boolean;
  /** Mostrar setas de navegação */
  showArrows?: boolean;
  /** Classe CSS adicional */
  className?: string;
  /** Habilitar fullscreen no mobile */
  enableFullscreen?: boolean;
}

/**
 * Componente ImageGallery
 * 
 * Galeria de imagens reutilizável com:
 * - Imagem principal com thumbnails abaixo
 * - Navegação por setas e gestos touch (swipe)
 * - Indicadores de posição (dots)
 * - Zoom na imagem principal (click ou pinch-to-zoom)
 * - Lazy loading para thumbnails
 * 
 * Valida: Requisitos 4.4, 4.5
 */
export function ImageGallery({
  images,
  boxTitle = 'Imagem',
  initialIndex = 0,
  onImageChange,
  enableZoom = true,
  showDots = true,
  showThumbnails = true,
  showArrows = true,
  className = '',
  enableFullscreen = true,
}: ImageGalleryProps): React.ReactElement {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const mainImageRef = useRef<HTMLDivElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // Detecta se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Minimum swipe distance to trigger navigation (in pixels)
  const minSwipeDistance = 50;

  // Reset index when images change
  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < images.length) {
      setCurrentIndex(initialIndex);
    } else {
      setCurrentIndex(0);
    }
    setIsZoomed(false);
  }, [images, initialIndex]);

  // Notify parent of image change
  useEffect(() => {
    onImageChange?.(currentIndex);
  }, [currentIndex, onImageChange]);

  // Scroll thumbnail into view when current index changes
  useEffect(() => {
    if (thumbnailsRef.current && showThumbnails) {
      const thumbnails = thumbnailsRef.current.querySelectorAll('.image-gallery__thumbnail');
      const currentThumbnail = thumbnails[currentIndex] as HTMLElement;
      if (currentThumbnail) {
        currentThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [currentIndex, showThumbnails]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setIsZoomed(false);
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setIsZoomed(false);
  }, [images.length]);

  const goToIndex = useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      setCurrentIndex(index);
      setIsZoomed(false);
    }
  }, [images.length]);

  // Fullscreen handlers
  const openFullscreen = useCallback(() => {
    if (enableFullscreen && isMobile) {
      setIsFullscreen(true);
      document.body.style.overflow = 'hidden';
    }
  }, [enableFullscreen, isMobile]);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    // Modal de detalhes continua aberto: mantém o scroll do body travado
    document.body.style.overflow = 'hidden';
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (images.length <= 1) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        case 'Escape':
          if (isFullscreen) {
            closeFullscreen();
          } else if (isZoomed) {
            setIsZoomed(false);
          }
          break;
      }
    },
    [images.length, isZoomed, isFullscreen, goToPrevious, goToNext, closeFullscreen]
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isZoomed) return;
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || isZoomed) return;
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || isZoomed) {
      setIsDragging(false);
      return;
    }

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

    if (isHorizontalSwipe && Math.abs(distanceX) > minSwipeDistance) {
      if (distanceX > 0) {
        // Swipe left - go to next
        goToNext();
      } else {
        // Swipe right - go to previous
        goToPrevious();
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
  };

  // Zoom handlers
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // No mobile, abre fullscreen ao invés de zoom
    if (isMobile && enableFullscreen && !isFullscreen) {
      openFullscreen();
      return;
    }
    
    if (!enableZoom) return;
    
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x, y });
      setIsZoomed(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isZoomed || !enableZoom) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handleMouseLeave = () => {
    if (isZoomed) {
      setIsZoomed(false);
    }
  };

  // Handle double tap for zoom on touch devices
  const lastTapRef = useRef<number>(0);
  const handleDoubleTap = (e: React.TouchEvent) => {
    if (!enableZoom) return;
    
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap detected
      e.preventDefault();
      if (isZoomed) {
        setIsZoomed(false);
      } else {
        const touch = e.changedTouches[0];
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 100;
        const y = ((touch.clientY - rect.top) / rect.height) * 100;
        setZoomPosition({ x, y });
        setIsZoomed(true);
      }
    }
    lastTapRef.current = now;
  };

  // No images placeholder
  if (!images || images.length === 0) {
    return (
      <div className={`image-gallery image-gallery--empty ${className}`}>
        <div className="image-gallery__no-image">
          <span className="image-gallery__no-image-icon">📖</span>
          <span className="image-gallery__no-image-text">
            Sem imagens disponíveis
          </span>
        </div>
      </div>
    );
  }

  const hasMultipleImages = images.length > 1;

  return (
    <div className={`image-gallery ${className}`}>
      {/* Main Image Container */}
      <div
        ref={mainImageRef}
        className={`image-gallery__main-container ${isZoomed ? 'image-gallery__main-container--zoomed' : ''} ${isDragging ? 'image-gallery__main-container--dragging' : ''}`}
        onClick={handleImageClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => {
          handleTouchEnd();
          handleDoubleTap(e);
        }}
        role="img"
        aria-label={`${boxTitle} - Imagem ${currentIndex + 1} de ${images.length}`}
        tabIndex={0}
      >
        {/* Carousel com todas as imagens */}
        <div 
          className="image-gallery__carousel"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <div key={image.id} className="image-gallery__slide">
              <img
                src={getDetailImageUrl(image.filepath)}
                alt={`${boxTitle} - Imagem ${index + 1}`}
                className="image-gallery__main-image"
                style={
                  isZoomed && index === currentIndex
                    ? {
                        transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        transform: 'scale(2.5)',
                      }
                    : undefined
                }
                draggable={false}
                decoding="async"
                onError={(e) => handleImageFallback(e, getImageUrl(image.filepath))}
              />
            </div>
          ))}
        </div>

        {/* Zoom indicator */}
        {enableZoom && !isZoomed && (
          <div className="image-gallery__zoom-hint">
            <span className="image-gallery__zoom-hint-icon">🔍</span>
            <span className="image-gallery__zoom-hint-text">Clique para zoom</span>
          </div>
        )}

        {/* Navigation Arrows */}
        {showArrows && hasMultipleImages && !isZoomed && (
          <>
            <button
              type="button"
              className="image-gallery__nav-button image-gallery__nav-button--prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              aria-label="Imagem anterior"
            >
              ‹
            </button>
            <button
              type="button"
              className="image-gallery__nav-button image-gallery__nav-button--next"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Próxima imagem"
            >
              ›
            </button>
          </>
        )}

        {/* Image Counter */}
        {hasMultipleImages && (
          <div className="image-gallery__counter">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Position Indicators (Dots) */}
      {showDots && hasMultipleImages && (
        <div className="image-gallery__dots" role="tablist" aria-label="Navegação de imagens">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`image-gallery__dot ${index === currentIndex ? 'image-gallery__dot--active' : ''}`}
              onClick={() => goToIndex(index)}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Ir para imagem ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Thumbnails */}
      {showThumbnails && hasMultipleImages && (
        <div
          ref={thumbnailsRef}
          className="image-gallery__thumbnails"
          role="tablist"
          aria-label="Miniaturas das imagens"
        >
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              className={`image-gallery__thumbnail ${index === currentIndex ? 'image-gallery__thumbnail--active' : ''}`}
              onClick={() => goToIndex(index)}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Ver imagem ${index + 1}`}
            >
              <img
                src={getCardImageUrl(image.filepath)}
                alt={`Miniatura ${index + 1}`}
                loading="lazy"
                decoding="async"
                draggable={false}
                onError={(e) => handleImageFallback(e, getImageUrl(image.filepath))}
              />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Modal (Mobile) */}
      {isFullscreen && (
        <div className="image-gallery__fullscreen-overlay">
          <button
            type="button"
            className="image-gallery__fullscreen-close"
            onClick={closeFullscreen}
            aria-label="Fechar visualização em tela cheia"
          >
            ✕
          </button>

          <div 
            className="image-gallery__fullscreen-content"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Carousel no fullscreen */}
            <div 
              className="image-gallery__fullscreen-carousel"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {images.map((image, index) => (
                <div key={image.id} className="image-gallery__fullscreen-slide">
                  <img
                    src={getImageUrl(image.filepath)}
                    alt={`${boxTitle} - Imagem ${index + 1}`}
                    className="image-gallery__fullscreen-image"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Fullscreen Navigation */}
          {hasMultipleImages && (
            <>
              <button
                type="button"
                className="image-gallery__fullscreen-nav image-gallery__fullscreen-nav--prev"
                onClick={goToPrevious}
                aria-label="Imagem anterior"
              >
                ‹
              </button>
              <button
                type="button"
                className="image-gallery__fullscreen-nav image-gallery__fullscreen-nav--next"
                onClick={goToNext}
                aria-label="Próxima imagem"
              >
                ›
              </button>
            </>
          )}

          {/* Fullscreen Counter */}
          {hasMultipleImages && (
            <div className="image-gallery__fullscreen-counter">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* Fullscreen Dots */}
          {hasMultipleImages && (
            <div className="image-gallery__fullscreen-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`image-gallery__fullscreen-dot ${index === currentIndex ? 'image-gallery__fullscreen-dot--active' : ''}`}
                  onClick={() => goToIndex(index)}
                  aria-label={`Ir para imagem ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ImageGallery;
